import {GenericSidebarItemBadgeComponent} from '@/components/_domain/SidebarItemBadges/SidebarItemBadge';
import {Pill} from '@ark/web';
import {Box, Typography} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {DateTime} from 'luxon';
import React from 'react';

const isNewFeature = (releaseDate?: DateTime): boolean => {
  if (!releaseDate) return false;
  const now = DateTime.now();
  const twoWeeksAfterRelease = releaseDate.plus({weeks: 2});
  return now <= twoWeeksAfterRelease;
};

export type SubSidebarItemProps = {
  disabled?: boolean;
  icon: React.ReactNode;
  label: string;
  selected?: boolean;
  handleClick: () => void;
  children?: React.ReactNode;
  BadgeComponent?: GenericSidebarItemBadgeComponent;
  releaseDate?: DateTime;
};

export const SubSidebarItem = (props: SubSidebarItemProps) => {
  const theme = useTheme();

  const item = React.useMemo(() => {
    return (
      <Box
        onClick={props.handleClick}
        sx={{
          display: 'flex',
          width: '100%',
          px: 1,
          py: 0.75,
          alignItems: 'center',
          cursor: props.disabled ? 'not-allowed' : 'pointer',
          borderRadius: '6px',
          justifyContent: 'space-between',
          mb: 0.25,
          backgroundColor: props.selected
            ? theme.palette.action.selected
            : 'transparent',
          color: props.disabled
            ? theme.palette.text.disabled
            : theme.palette.text.primary,
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: props.selected
              ? theme.palette.action.selected
              : theme.palette.action.hover,
          },
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
          <Box
            sx={{
              display: 'flex',
              width: 24,
              height: 24,
              justifyContent: 'center',
              alignItems: 'center',
              flexShrink: 0,
              '& svg, & .material-symbols-outlined': {
                fontSize: 18,
                color: props.selected
                  ? theme.palette.text.primary
                  : theme.palette.text.secondary,
              },
            }}
          >
            {props.BadgeComponent ? (
              <props.BadgeComponent
                onClick={e => {
                  e.stopPropagation();
                  props.handleClick();
                }}
              >
                {props.icon}
              </props.BadgeComponent>
            ) : (
              props.icon
            )}
          </Box>
          <Typography
            variant="body2"
            sx={{
              fontSize: 13,
              fontWeight: props.selected ? 500 : 400,
              lineHeight: 1.2,
              letterSpacing: '0.1px',
              color: props.selected
                ? theme.palette.text.primary
                : theme.palette.text.secondary,
            }}
          >
            {props.label}
          </Typography>
        </Box>
        {isNewFeature(props.releaseDate) && (
          <Pill variant="feature" label="New" />
        )}
        {props.children}
      </Box>
    );
  }, [props, theme]);

  return item;
};
